import { useParams, useRouter } from "@tanstack/react-router";
import { ArrowLeft, Download, Eye, FileText, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCommitment } from "@/hooks/use-commitments";
import { useSubPayApps } from "@/hooks/use-pay-applications";

function fmt(value: string | number | null | undefined): string {
  if (value == null) return "--";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(num);
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return "--";
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function statusVariant(
  status: string | null | undefined,
): "success" | "warning" | "secondary" | "destructive" | "default" {
  switch (status) {
    case "active":
    case "approved":
    case "executed":
      return "success";
    case "draft":
    case "pending":
    case "pending_execution":
      return "warning";
    case "completed":
    case "closed":
      return "secondary";
    case "cancelled":
    case "void":
    case "terminated":
      return "destructive";
    default:
      return "default";
  }
}

function contractFileName(path: string | null | undefined): string {
  if (!path) return "Contract document";
  const parts = path.split("/");
  const last = parts[parts.length - 1] ?? "contract-document";
  const dashIdx = last.indexOf("-");
  return dashIdx >= 0 ? last.substring(dashIdx + 1) : last;
}

function ContractDocumentSection({
  commitmentId,
  path,
}: {
  commitmentId: string;
  path: string | null | undefined;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (!path) {
    return (
      <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        <FileText className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
        No contract document uploaded.
      </div>
    );
  }

  const fileName = contractFileName(path);
  const canPreview = path.toLowerCase().endsWith(".pdf");

  async function handlePreview() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      return;
    }
    setLoadingPreview(true);
    try {
      const res = await fetch(`/api/erp-commitments/${commitmentId}/contract-document`);
      if (!res.ok) throw new Error(`Failed to load document: ${res.status}`);
      const blob = await res.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load preview");
    } finally {
      setLoadingPreview(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 rounded-md border px-3 py-2">
        <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{fileName}</p>
          <p className="text-xs text-muted-foreground">Contract document</p>
        </div>
        {canPreview && (
          <Button variant="outline" size="sm" onClick={handlePreview} disabled={loadingPreview}>
            <Eye className="mr-1.5 h-4 w-4" />
            {previewUrl ? "Hide" : loadingPreview ? "Loading..." : "Preview"}
          </Button>
        )}
        <a href={`/api/erp-commitments/${commitmentId}/contract-document`} download={fileName}>
          <Button variant="default" size="sm">
            <Download className="mr-1.5 h-4 w-4" />
            Download
          </Button>
        </a>
      </div>
      {previewUrl && canPreview && (
        <div className="overflow-hidden rounded-md border">
          <iframe src={previewUrl} title={fileName} className="h-[60vh] w-full" />
        </div>
      )}
      {!canPreview && (
        <p className="text-xs text-muted-foreground">
          Inline preview is only available for PDF files. Use Download to view other formats.
        </p>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

export function CommitmentDetailPage() {
  const { id } = useParams({ strict: false });
  const router = useRouter();
  const commitmentId = id ?? "";
  const { data: commitment, isLoading, isError, error, refetch } = useCommitment(commitmentId);
  const { data: payApps, isLoading: payAppsLoading } = useSubPayApps(
    commitment?.projectId ? { projectId: commitment.projectId } : undefined,
  );

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load commitment"}
        onRetry={() => refetch()}
      />
    );
  }

  if (!commitment) {
    return <ErrorState message="Commitment not found" />;
  }

  const relatedPayApps = (payApps ?? []).filter((pa) => pa.commitmentId === commitmentId);
  const lineItems = commitment.lineItems ?? [];

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      {/* Page header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => router.history.back()}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-gray-900">
                {commitment.commitmentNumber ?? "Commitment"}
              </h1>
              <Badge variant={statusVariant(commitment.status)}>
                {commitment.status?.replace(/_/g, " ") ?? "--"}
              </Badge>
            </div>
            <p className="text-sm text-gray-500">
              {commitment.title ??
                commitment.commitmentType?.replace(/_/g, " ") ??
                "Commitment details"}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              router.navigate({ to: "/commitments/$id/edit", params: { id: commitmentId } })
            }
          >
            <Pencil className="mr-1.5 h-4 w-4" />
            Edit
          </Button>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl space-y-6 px-6 py-6">
          {/* Details */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-700">Details</h2>
            <div className="grid grid-cols-2 gap-4 rounded-lg border bg-white p-4 md:grid-cols-3">
              <Field label="Type" value={commitment.commitmentType?.replace(/_/g, " ") ?? "--"} />
              <Field
                label="Status"
                value={
                  <Badge variant={statusVariant(commitment.status)}>
                    {commitment.status?.replace(/_/g, " ") ?? "--"}
                  </Badge>
                }
              />
              <Field label="Company" value={commitment.companyName ?? "--"} />
              <Field label="Start Date" value={fmtDate(commitment.startDate)} />
              <Field label="Scheduled End" value={fmtDate(commitment.scheduledEndDate)} />
              <Field
                label="Retention"
                value={commitment.retentionPercent ? `${commitment.retentionPercent}%` : "--"}
              />
            </div>
          </section>

          <Separator />

          {/* Financial summary */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-700">Financial Summary</h2>
            <div className="grid grid-cols-2 gap-4 rounded-lg border bg-white p-4 md:grid-cols-3">
              <Field label="Original Amount" value={fmt(commitment.originalAmount)} />
              <Field label="Approved Changes" value={fmt(commitment.approvedChangesAmount)} />
              <Field
                label="Revised Amount"
                value={<span className="font-bold">{fmt(commitment.revisedAmount)}</span>}
              />
              <Field label="Pending Changes" value={fmt(commitment.pendingChangesAmount)} />
              <Field label="Billed to Date" value={fmt(commitment.totalBilledToDate)} />
              <Field label="Paid to Date" value={fmt(commitment.totalPaidToDate)} />
            </div>
          </section>

          {/* G703 Schedule of Values */}
          {lineItems.length > 0 && (
            <>
              <Separator />
              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-gray-700">
                  Schedule of Values (G703) — {lineItems.length} line
                  {lineItems.length !== 1 ? "s" : ""}
                </h2>
                <div className="overflow-x-auto rounded-lg border bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                          Item #
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                          Description
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                          Cost Code
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                          Scheduled Value
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((li) => (
                        <tr key={li.id} className="border-t">
                          <td className="px-3 py-2 text-gray-900">{li.itemNo}</td>
                          <td className="px-3 py-2 text-gray-900">{li.description}</td>
                          <td className="px-3 py-2 text-gray-500">
                            {li.costCode ? (
                              <span>
                                <span className="font-medium text-gray-900">{li.costCode}</span>
                                {li.costCodeDescription ? ` — ${li.costCodeDescription}` : ""}
                              </span>
                            ) : (
                              "--"
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-gray-900">
                            {fmt(li.scheduledValue)}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t bg-gray-50">
                        <td
                          colSpan={3}
                          className="px-3 py-2 text-right text-xs font-semibold text-gray-600"
                        >
                          Schedule Total
                        </td>
                        <td className="px-3 py-2 text-right text-sm font-semibold text-gray-900">
                          {fmt(
                            String(lineItems.reduce((s, li) => s + Number(li.scheduledValue), 0)),
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          <Separator />

          {/* Contract document */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-700">Contract Document</h2>
            <ContractDocumentSection
              commitmentId={commitment.id}
              path={commitment.contractDocumentPath}
            />
          </section>

          {/* Pay application history */}
          <Separator />
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-700">Pay Application History</h2>
            {payAppsLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : relatedPayApps.length === 0 ? (
              <div className="rounded-lg border border-dashed py-8 text-center text-sm text-muted-foreground">
                No pay applications submitted yet.
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border bg-white">
                <Table>
                  <TableHeader className="bg-gray-50">
                    <TableRow>
                      <TableHead>App No.</TableHead>
                      <TableHead>Period To</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total Completed</TableHead>
                      <TableHead className="text-right">Net Payment</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {relatedPayApps.map((pa) => (
                      <TableRow key={pa.id}>
                        <TableCell className="font-medium">{pa.payAppNumber ?? "--"}</TableCell>
                        <TableCell>{fmtDate(pa.billingPeriodEnd)}</TableCell>
                        <TableCell>
                          <Badge variant={statusVariant(pa.status)}>{pa.status ?? "--"}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{fmt(pa.totalCompletedStored)}</TableCell>
                        <TableCell className="text-right font-medium">
                          {fmt(pa.currentPaymentDue)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
